import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Dropdown } from 'components/UIElements'
import { modalActions } from 'store/modal'
import { tmgKeyFormatInfoIcon, tmgUploadSymbolsZipIcon } from 'assets/assets'
import STRINGS from 'utils/strings'
import {
  QUESTION_TYPE_MAP,
  TIMED_MATCHING_GRID_INPUT_TYPE_OPTIONS,
  TIMED_MATCHING_GRID_KEY_MAPPING_OPTIONS,
  TIMED_MATCHING_GRID_KEY_SLOT_COUNT,
} from 'utils/constants'
import {
  applyTimedMatchingGridDetailsPatch,
  getTimedMatchingGridDefaults,
  getTimedMatchingGridDetails,
  hasCompleteSymbolKey,
} from 'utils/timedMatchingGrid'
import SymbolKeyUploadDialog from './SymbolKeyUploadDialog'
import TimedMatchingGridPreview from './TimedMatchingGridPreview'
import TimedMatchingGridSymbol from './TimedMatchingGridSymbol'
import './TimedMatchingGrid.scss'

const TimedMatchingGridView = ({
  clearQuestionError,
  closeModal,
  item,
  itemId,
  openModal,
  preview,
  setTimedMatchingGridSymbolKey,
  studyID,
  updateItem,
  uploadImage,
}) => {
  const zipInputRef = useRef(null)

  useEffect(() => {
    if (!updateItem || preview) return
    if (item.type !== QUESTION_TYPE_MAP.timedMatchingGrid) return
    if (!item.attributes?.timed_matching_grid_details) {
      updateItem(itemId, {
        ...item,
        attributes: {
          ...item.attributes,
          in_review: false,
          timed_matching_grid_details: getTimedMatchingGridDefaults(),
        },
      })
    }
  }, [updateItem, item, itemId, preview])

  const details = getTimedMatchingGridDetails(item)

  if (preview) {
    return <TimedMatchingGridPreview item={item} />
  }

  const disabled = false
  const keyComplete = hasCompleteSymbolKey(details.symbol_key)

  const updateDetails = (patch, options) => {
    if (!updateItem) return
    const nextDetails = applyTimedMatchingGridDetailsPatch(details, patch, options)
    updateItem(itemId, {
      ...item,
      attributes: {
        ...item.attributes,
        in_review: false,
        timed_matching_grid_details: nextDetails,
      },
    })
    if (clearQuestionError) clearQuestionError(itemId)
  }

  const openUploadDialog = initialFile => {
    openModal({
      className: 'tmg-symbol-key-modal',
      width: '780px',
      noButtons: true,
      closeOnBackgroundClick: true,
      content: (
        <SymbolKeyUploadDialog
          initialFile={initialFile || null}
          slotCount={TIMED_MATCHING_GRID_KEY_SLOT_COUNT}
          studyID={studyID}
          uploadImage={uploadImage}
          onCancel={closeModal}
          onSave={committedSymbols => {
            // Drop previous local blob previews when replacing
            ;(details.symbol_key || []).forEach(entry => {
              if (entry?.imageSrc && String(entry.imageSrc).startsWith('blob:')) {
                URL.revokeObjectURL(entry.imageSrc)
              }
            })
            if (setTimedMatchingGridSymbolKey) {
              setTimedMatchingGridSymbolKey(itemId, committedSymbols)
            } else {
              const version = (details.symbol_key_version || 0) + 1
              updateDetails({
                symbol_key: committedSymbols.map(s => ({
                  digit: s.digit,
                  image: s.image,
                  imageSrc: s.imageSrc,
                  version,
                })),
                symbol_key_version: version,
              })
            }
            closeModal()
          }}
        />
      ),
    })
  }

  const onUploadClick = () => {
    if (!zipInputRef.current) return
    zipInputRef.current.value = ''
    zipInputRef.current.click()
  }

  const onZipInputChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
    openUploadDialog(file)
  }

  const renderKeySlots = () => (
    <div className='tmg-key-slots'>
      {Array.from({ length: TIMED_MATCHING_GRID_KEY_SLOT_COUNT }, (_, i) => {
        const digit = i + 1
        const entry = (details.symbol_key || []).find(s => Number(s.digit) === digit)
        return (
          <div key={digit} className='tmg-key-slot'>
            <div className='tmg-key-slot-image'>
              <TimedMatchingGridSymbol entry={entry} alt={`Symbol ${digit}`} />
            </div>
            <div className='tmg-key-slot-digit'>{digit}</div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className='timed-matching-grid-view'>
      <input
        ref={zipInputRef}
        type='file'
        accept='.zip,application/zip,application/x-zip-compressed'
        className='tmg-hidden-file-input'
        onChange={onZipInputChange}
      />
      <div className='tmg-symbol-key-panel panel'>
        <div className='tmg-symbol-key-header'>
          <div className='tmg-symbol-key-title-block'>
            <h5 className='label-small med-font'>{STRINGS.tmgKeyPairsTitle}</h5>
            <span className='tmg-key-pairs-hint'>{STRINGS.tmgKeyPairsHint}</span>
          </div>
          {!disabled && (
            <Button
              link
              no-margin
              className='tmg-upload-zip-link'
              content={
                <>
                  <span className='tmg-upload-icon'>{tmgUploadSymbolsZipIcon}</span>
                  {keyComplete ? STRINGS.tmgReplaceSymbolsZip : STRINGS.tmgUploadSymbolsZip}
                </>
              }
              onClick={onUploadClick}
            />
          )}
        </div>
        {renderKeySlots()}
        <p className='tmg-key-format-note'>
          <span className='tmg-key-format-info-icon'>{tmgKeyFormatInfoIcon}</span>
          <span>{STRINGS.tmgKeyFormatNote}</span>
        </p>
      </div>

      <div className='tmg-config-row'>
        <div className='tmg-field'>
          <h5 className='label-small med-font'>{STRINGS.tmgKeyMapping}</h5>
          <Dropdown
            selected={details.key_mapping}
            options={TIMED_MATCHING_GRID_KEY_MAPPING_OPTIONS}
            disabled
            onSelect={() => {}}
          />
        </div>
        <div className='tmg-field'>
          <h5 className='label-small med-font'>{STRINGS.tmgInputType}</h5>
          <Dropdown
            selected={details.input_type}
            options={TIMED_MATCHING_GRID_INPUT_TYPE_OPTIONS}
            disabled={disabled}
            onSelect={opt => updateDetails({ input_type: opt.value })}
          />
        </div>
      </div>
    </div>
  )
}

TimedMatchingGridView.propTypes = {
  clearQuestionError: PropTypes.func,
  closeModal: PropTypes.func,
  item: PropTypes.shape({
    type: PropTypes.string,
    attributes: PropTypes.shape({
      timed_matching_grid_details: PropTypes.shape({}),
      in_review: PropTypes.bool,
    }),
  }),
  itemId: PropTypes.string,
  openModal: PropTypes.func,
  preview: PropTypes.bool,
  setTimedMatchingGridSymbolKey: PropTypes.func,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updateItem: PropTypes.func,
  uploadImage: PropTypes.func,
}

const mapStateToProps = state => ({
  studyID: state.study?.currentStudy?.id,
})

const mapDispatchToProps = {
  openModal: modalActions.openModal,
  closeModal: modalActions.closeModal,
}

// mergeProps keeps itemActions (e.g. setTimedMatchingGridSymbolKey) from SurveyItemContainer
export default connect(mapStateToProps, mapDispatchToProps)(TimedMatchingGridView)
